import { FormattedMessage } from 'react-intl';
import { Button, FormControlLabel, Switch, Stack } from '@mui/material';
import { DarkMode, LightMode } from '@mui/icons-material';
import { Link, useParams } from 'react-router';
import { graphql, useFragment } from 'react-relay';
import { tss } from 'tss-react/mui';
import { LogoutState, useLogout } from '@/services/auth';
import { useDarkMode } from '@/utils/useDarkMode';
import isAdminEmail from '@/utils/isAdminEmail';
import { Beta, useBeta } from '@/utils/useBeta';
import { getPortfolioFullPath } from '@/RootPaths';
import type { RouteParams } from '@/RootPaths';
import { Label } from '../Label';
import { Paragraph } from '../Paragraph';
import { ProfileIcon } from '../icons';
import type { ProfilePopper_user$key } from './__generated__/ProfilePopper_user.graphql';

const useProfilePopperStyles = tss.create(({ theme }) => ({
    profileIcon: {
        position: 'absolute',
        color: theme.palette.text.secondary,
    },
    profilePopper: {
        maxWidth: 400,
    },
    indent: {
        paddingLeft: 48,
        paddingRight: 24,
    },
    profileFullName: {
        fontWeight: 'bold',
        fontSize: '24px',
        lineHeight: '28px',
        color: theme.palette.text.primary,
    },
    profileEmailAddress: {
        fontWeight: 'normal',
        fontSize: '14px',
        lineHeight: '22px',
        color: theme.palette.text.primary,
    },
    profileAccessType: {
        display: 'block',
        margin: `16px 0`,
        textTransform: 'uppercase',
    },
    profileLogOutButton: {
        zIndex: 15,
    },
}));

export function ProfilePopper(props: {
    close(): void;
    user: ProfilePopper_user$key;
    showSwitchTeamLink?: boolean;
}) {
    const user = useFragment(fragments.user, props.user);
    const { classes } = useProfilePopperStyles();
    const { showSwitchTeamLink = false } = props;
    const [logoutState, logout] = useLogout();
    const isAdmin = isAdminEmail(user.emailAddress);
    const [beta, setBeta] = useBeta();
    const showDarkMode = beta; //user.emailAddress === 'marcus.grymmare@binosight.com';
    const [darkMode, setDarkMode] = useDarkMode();
    const params = useParams<RouteParams>();
    const portfolioPath = getPortfolioFullPath(params);
    const isTeamSelectPage = portfolioPath === '';

    return (
        <div className={classes.profilePopper}>
            <ProfileIcon className={classes.profileIcon} size={32} />
            <div className={classes.indent}>
                <div className={classes.profileFullName}>{user.fullName}</div>
                <div className={classes.profileEmailAddress}>
                    {user.emailAddress}
                </div>
                {isAdmin && (
                    <Label className={classes.profileAccessType}>
                        <FormattedMessage defaultMessage="Admin Account" />
                    </Label>
                )}
                <Stack>
                    {isAdmin && (
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={beta}
                                    onChange={() => setBeta(!beta)}
                                />
                            }
                            labelPlacement="start"
                            label={<Label>Beta</Label>}
                        />
                    )}
                    {showDarkMode && (
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={darkMode}
                                    checkedIcon={
                                        <DarkMode
                                            fontSize="small"
                                            sx={(theme) => ({
                                                backgroundColor:
                                                    theme.palette.primary.main,
                                                borderRadius: '50%',
                                                color: theme.bino.color
                                                    .lightGray,
                                                boxShadow:
                                                    '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)',
                                            })}
                                        />
                                    }
                                    icon={
                                        <LightMode
                                            fontSize="small"
                                            sx={(theme) => ({
                                                backgroundColor:
                                                    theme.bino.color.white,
                                                borderRadius: '50%',
                                                color: theme.bino.color.gray,
                                                boxShadow:
                                                    '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)',
                                            })}
                                        />
                                    }
                                    onChange={() => setDarkMode(!darkMode)}
                                />
                            }
                            labelPlacement="start"
                            label={
                                <Beta
                                    color={(theme) =>
                                        theme.palette.text.secondary
                                    }
                                >
                                    <Label>Mörk läge</Label>
                                </Beta>
                            }
                        />
                    )}
                </Stack>
            </div>
            {isAdmin && !isTeamSelectPage && (
                <Button
                    component={Link}
                    to={`/${portfolioPath}/account`}
                    fullWidth
                    variant="text"
                    color="primary"
                    className={classes.profileLogOutButton}
                    onClick={props.close} // Ensure the popper closes
                >
                    <FormattedMessage defaultMessage="Organisation & Contracts" />
                </Button>
            )}
            <Button
                fullWidth
                variant="outlined"
                color="primary"
                className={classes.profileLogOutButton}
                onClick={logout}
                disabled={logoutState === LogoutState.LoggingOut}
            >
                <FormattedMessage defaultMessage="Log out" />
            </Button>
            {logoutState === LogoutState.Failed && <div>LOGOUT FAILED</div>}

            {showSwitchTeamLink && (
                <div>
                    <Paragraph>
                        <FormattedMessage defaultMessage="It seems that you have access to multiple teams. " />
                        <Link to={'/'}>
                            <FormattedMessage defaultMessage="Switch team" />
                        </Link>
                    </Paragraph>
                </div>
            )}
        </div>
    );
}

const fragments = {
    user: graphql`
        fragment ProfilePopper_user on User {
            id
            fullName
            emailAddress
        }
    `,
};
